/**
 * The report's content, as data.
 *
 * WHY THIS EXISTS. The plan is rendered twice — on screen in React and into a
 * PDF with jsPDF — and the two cannot share a renderer. What they can share is
 * everything up to the renderer: which figures a section shows, what each is
 * labelled, how each is formatted, which rows a table has. That used to live
 * inside each renderer, and the result was two documents that disagreed: six
 * KPI tiles on screen against four in the PDF, a different money format, a
 * different order of sections.
 *
 * So every section's content is built here, once, as plain objects. The PDF
 * draws them with `PdfCanvas`; the screen renders them as JSX. Neither decides
 * what a section contains, which is why they cannot drift apart.
 *
 * The three layers above this one are unchanged:
 *   report/derive  the plan resolved to facts
 *   report/rules   the facts resolved to sentences
 *   report/blocks  which sections exist, and what each is numbered
 */

import {
  liabilityMonthlySip,
} from "./analytics";
import type {
  PlanAnalytics,
} from "./analytics";
import { percent, smestWeightsText } from "./format";
import type { PlanFacts } from "./report/derive";
import {
  bondName,
  dateLong,
  groupIN,
  monthYear,
  monthYearShort,
  pluralWords,
  schemeName,
  tenureLong,
  words,
} from "./report/format";
import type { RetirementAdequacy } from "./retirement";
import type {
  PlanResult,
  PlannerFund,
  PlannerGoalResult,
  PlannerLiabilityResult,
} from "./types";

/* ────────────────────── the document's conventions ──────────────────── */

/**
 * Money, everywhere: "₹70,24,114.64".
 *
 * FULL FIGURE, ALWAYS TWO DECIMALS, no scale words. A plan is checked line by
 * line against statements, and an abbreviated figure cannot be checked at all.
 */
export const inr = (value: number): string => {
  const amount = Number(value) || 0;
  return `${amount < 0 ? "− " : ""}₹${groupIN(Math.abs(amount), 2)}`;
};

/** "22 January 2031". The document spells its months out. */
export const on = (iso?: string | null): string => dateLong(iso);

/** "AAA by CRISIL" → "AAA (CRISIL)" — the agency as attribution, not preposition. */
export const ratingLabel = (rating?: string | null): string | null => {
  if (!rating || !rating.trim()) return null;
  const match = rating.match(/^(.*?)\s+by\s+(.+)$/i);
  return match ? `${match[1].replace(/\s+/g, "")} (${match[2].trim()})` : rating.trim();
};

/**
 * "an AAA-rated bond", "a BBB-rated bond", "an REC bond".
 *
 * Sound, not spelling: an initialism is read letter by letter, so REC, SBI and
 * LIC open on a vowel sound despite starting with a consonant.
 */
const VOWEL_SOUND_LETTERS = /^[AEFHILMNORSX]/;

export const article = (phrase: string): string => {
  const word = phrase.trim().split(/[\s-]/)[0] ?? "";
  if (!word) return "a";
  const isInitialism =
    word.length > 1 && word === word.toUpperCase() && /^[A-Z]+$/.test(word);
  if (isInitialism) return VOWEL_SOUND_LETTERS.test(word) ? "an" : "a";
  if (/^\d/.test(word)) return /^(8|11|18)/.test(word) ? "an" : "a";
  return /^[aeiou]/i.test(word) ? "an" : "a";
};

/** "7.79% REC Limited — Call 31 Jan 2031" → "REC Limited". */
export const bondIssuer = (name: string): string => {
  const stripped = bondName(name)
    .replace(/^[\d.]+%\s*/, "")
    .replace(/\s*[—–-]\s*(Call|Put|Maturity)\b.*$/i, "")
    .replace(/\s+\d{1,2}\s+\w+\s+\d{4}\s*$/, "")
    .trim();
  return stripped || "the issuer";
};

/** Just the recognisable word, for running prose: "an REC bond". */
export const bondIssuerWord = (name: string): string =>
  bondIssuer(name).split(/\s+/)[0] || "corporate";

const PART_WORDS = ["One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight"];
export const partWord = (index: number): string =>
  PART_WORDS[index] ?? String(index + 1);

export const tenureFromYears = (years: number): string => {
  const whole = Math.floor(years);
  const months = Math.round((years - whole) * 12);
  if (!whole) return `${months}m`;
  return months ? `${whole}y ${months}m` : `${whole}y`;
};

/* ────────────────────────────── date helpers ────────────────────────── */

const MONTHS_PER_PERIOD: Record<string, number> = {
  Monthly: 1,
  Quarterly: 3,
  "Half-Yearly": 6,
  "Half Yearly": 6,
  "Semi-Annual": 6,
  Yearly: 12,
  Annual: 12,
  Annually: 12,
};

export const addMonths = (iso: string, delta: number): Date => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + delta);
  return date;
};

export const isoDate = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * The last coupon before settlement, and the days of interest owed for it.
 *
 * The API returns the accrued rupees but neither the date they ran from nor the
 * day count, and "you pay ₹44,763 of accrued interest" means nothing without
 * them. Both are recoverable: `cashflows[0]` is the next coupon, so one period
 * back is the last one.
 */
export const accrualWindow = (
  firstCouponIso: string | undefined,
  frequency: string,
  settlementIso: string
): { from: Date; days: number } | null => {
  const period = MONTHS_PER_PERIOD[frequency];
  if (!firstCouponIso || !period) return null;
  const from = addMonths(firstCouponIso, -period);
  const days = Math.round(
    (new Date(`${settlementIso}T00:00:00Z`).getTime() - from.getTime()) / 86_400_000
  );
  return days > 0 && days < 400 ? { from, days } : null;
};

/** "Sep 26 – Jan 31" — the window a run of instalments occupies. */
export const instalmentWindow = (startIso: string, endIso: string): string =>
  `${monthYearShort(startIso)} – ${monthYearShort(endIso)}`;

/* ─────────────────────────── shared shapes ──────────────────────────── */

export interface StatTile {
  label: string;
  value: string;
  caption?: string;
  /** Reserved for the one figure that needs attention. */
  flagged?: boolean;
}

export interface FactRow {
  label: string;
  value: string;
}

export interface LedgerRow {
  label: string;
  note?: string;
  value: string;
  negative?: boolean;
}

export interface TableSpec {
  caption?: string;
  columns: {
    header: string;
    align?: "left" | "right";
    width: number;
    /** Set smaller and muted — the prose column beside a set of figures. */
    subtle?: boolean;
  }[];
  rows: TableCellSpec[][];
  footnote?: string;
}

export interface TableCellSpec {
  text: string;
  note?: string;
  strong?: boolean;
  muted?: boolean;
  alarm?: boolean;
}

const cell = (
  text: string,
  extra: Omit<TableCellSpec, "text"> = {}
): TableCellSpec => ({ text, ...extra });

/** Everything a section builder needs. Assembled once per render. */
export interface ReportContext {
  plan: PlanResult;
  facts: PlanFacts;
  analytics: PlanAnalytics;
  adequacy: RetirementAdequacy | null;
}

/* ────────────────────────────── the cover ───────────────────────────── */

/**
 * "Two savings goals, one car loan and a retirement drawdown, reviewed
 * together." Counted, never written — a plan with no loan must not mention one.
 */
export const coverStandfirst = (facts: PlanFacts): string => {
  const parts: string[] = [];
  if (facts.goalCount > 0) {
    parts.push(pluralWords(facts.goalCount, "savings goal", "savings goals"));
  }
  if (facts.liabilityCount > 0) {
    parts.push(
      pluralWords(facts.liabilityCount, facts.liabilityNoun, `${facts.liabilityNoun}s`)
    );
  }
  if (facts.hasSwp) parts.push("a retirement drawdown");
  if (!parts.length) return "A plan with nothing yet to fund.";
  const joined =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `${joined[0].toUpperCase()}${joined.slice(1)}, reviewed together`;
};

export const coverFacts = (plan: PlanResult): FactRow[] => {
  const client = plan.client;
  return [
    {
      label: "Age",
      value: `${client.ageY} years${client.ageM ? ` ${client.ageM} months` : ""}`,
    },
    { label: "Income type", value: client.type },
    { label: "Risk profile", value: client.risk },
    {
      label: "Tax slab",
      value: client.taxBracket ? `${client.taxBracket}%` : "No tax",
    },
    { label: "Shariah compliant", value: client.shariah ? "Yes" : "No" },
    { label: "First time investor", value: client.firstTimer ? "Yes" : "No" },
  ];
};

/* ───────────────────────── 01 · plan at a glance ────────────────────── */

export const glanceTiles = (ctx: ReportContext): StatTile[] => {
  const { plan, facts, analytics, adequacy } = ctx;
  const goalSip = plan.goals.reduce(
    (sum, _goal, index) => sum + analytics.goals[index].monthlySip,
    0
  );
  const loanSip = plan.liabilities.reduce(
    (sum, liability) => sum + liabilityMonthlySip(liability),
    0
  );

  return [
    {
      label: "Goal SIPs",
      value: inr(goalSip),
      caption: pluralWords(facts.goalCount, "goal", "goals"),
    },
    ...(loanSip > 0
      ? [
          {
            label: "Loan offset SIP",
            value: inr(loanSip),
            caption:
              facts.liabilityCount === 1
                ? facts.liabilities[0].label
                : pluralWords(facts.liabilityCount, "loan", "loans"),
          },
        ]
      : []),
    {
      label: "Total asked each month",
      value: inr(facts.committed),
      caption: facts.surplusRecorded
        ? `vs ${inr(facts.surplus)} surplus`
        : "no surplus recorded",
      flagged: facts.shortfall > 0,
    },
    ...(adequacy
      ? [
          {
            label: "Retirement corpus lasts",
            value: adequacy.sufficient
              ? "Indefinitely"
              : tenureFromYears(adequacy.lastsYears),
            caption: adequacy.sufficient
              ? "sustainable"
              : `from age ${Math.floor(adequacy.startAge)}`,
          },
        ]
      : []),
  ];
};

const goalSummaryLine = (goal: PlannerGoalResult): string => {
  const route = goal.bond
    ? `funded by ${article(bondIssuerWord(goal.bond.name))} ${bondIssuerWord(goal.bond.name)} bond plus a SIP`
    : goal.coveredByLumpsum > 0
      ? "funded by the lumpsum plus a SIP"
      : "SIP only";
  return `${inr(goal.costToday)} in today's money by ${monthYear(goal.goalDate)}, ${route}`;
};

const liabilitySummaryLine = (liability: PlannerLiabilityResult): string =>
  `${inr(liability.outstanding)} at ${percent(liability.rate, 0)} over ${tenureLong(liability.years, liability.months)}; a parallel SIP offsets the interest`;

/** "What this plan covers" — the contents page, in money. */
export const planCoversTable = (ctx: ReportContext): TableSpec => {
  const { plan, facts, analytics } = ctx;
  const rows: TableCellSpec[][] = [
    ...plan.goals.map((goal, index) => [
      cell(`${partWord(index)} — ${goal.name}`, { strong: true }),
      cell(goalSummaryLine(goal), { muted: true }),
      cell(inr(analytics.goals[index].monthlySip), { strong: true }),
    ]),
    ...plan.liabilities.map((liability, index) => [
      cell(`${partWord(plan.goals.length + index)} — ${liability.name}`, {
        strong: true,
      }),
      cell(liabilitySummaryLine(liability), { muted: true }),
      cell(inr(liabilityMonthlySip(liability)), { strong: true }),
    ]),
    ...(plan.swp
      ? [
          [
            cell(
              `${partWord(plan.goals.length + plan.liabilities.length)} — Retirement Income`,
              { strong: true }
            ),
            cell(
              `${inr(plan.swp.net)} a ${plan.swp.freq.toLowerCase().replace("ly", "")} drawn from ${monthYear(plan.swp.startDate)}`,
              { muted: true }
            ),
            cell("—", { muted: true }),
          ],
        ]
      : []),
    [
      cell("Total asked of your surplus", { strong: true }),
      cell(""),
      cell(inr(facts.committed), { strong: true }),
    ],
  ];

  return {
    caption: "What this plan covers",
    columns: [
      { header: "Part", width: 30 },
      { header: "What it is", width: 52, subtle: true },
      { header: "Monthly", width: 18, align: "right" },
    ],
    rows,
  };
};

/* ────────────────────────── 02 · goals overview ─────────────────────── */

export const goalsOverviewLede = (ctx: ReportContext): string | null => {
  const { plan, facts } = ctx;
  const longest = plan.goals.find((goal) => goal.isLongest);
  if (!longest || facts.lumpsum <= 0) return null;
  return `Your ${inr(facts.lumpsum)} lumpsum is allocated to a single goal — the longest one. ${longest.name} therefore gets ${longest.bond ? "the bond" : "it"}; the rest ${facts.goalCount > 2 ? "are" : "is"} funded entirely from monthly savings.`;
};

export const goalsOverviewTable = (ctx: ReportContext): TableSpec => {
  const { plan, analytics } = ctx;
  return {
    columns: [
      { header: "Goal", width: 26 },
      { header: "Segment", width: 15 },
      { header: "Target date", width: 14 },
      { header: "Needed then", width: 15, align: "right" },
      { header: "From lumpsum", width: 15, align: "right" },
      { header: "Monthly SIP", width: 15, align: "right" },
    ],
    rows: [
      ...plan.goals.map((goal, index) => [
        cell(goal.name, { note: `${inr(goal.costToday)} today`, strong: true }),
        cell(`${goal.seg} · ${percent(goal.ret, 0)}`, { muted: true }),
        cell(on(goal.goalDate), { muted: true }),
        cell(inr(goal.futureCost)),
        goal.coveredByLumpsum > 0
          ? cell(inr(goal.coveredByLumpsum))
          : cell(inr(0), { muted: true }),
        cell(inr(analytics.goals[index].monthlySip), { strong: true }),
      ]),
      [
        cell(`All ${words(plan.goals.length)} goals`, { strong: true }),
        cell(""),
        cell(""),
        cell(inr(plan.summary.totalFutureCost), { strong: true }),
        cell(
          inr(plan.goals.reduce((sum, goal) => sum + goal.coveredByLumpsum, 0)),
          { strong: true }
        ),
        cell(
          inr(
            plan.goals.reduce(
              (sum, _goal, index) => sum + analytics.goals[index].monthlySip,
              0
            )
          ),
          { strong: true }
        ),
      ],
    ],
  };
};

/* ─────────────────────────────── the goal ───────────────────────────── */

/** "44 years 7 months" — the age at a goal date. The SWP block carries none. */
export const ageAtLabel = (plan: PlanResult, monthsFromNow: number): string => {
  const total =
    plan.client.ageY * 12 + plan.client.ageM + Math.round(monthsFromNow);
  const years = Math.floor(total / 12);
  const months = total % 12;
  return months ? `${years} years ${months} months` : `${years} years`;
};

export const goalFacts = (
  ctx: ReportContext,
  goal: PlannerGoalResult
): FactRow[] => [
  { label: "Cost in today's money", value: inr(goal.costToday) },
  { label: "Inflation assumed", value: `${percent(goal.infl)} a year` },
  {
    label: "Time to goal",
    value: `${goal.totalMonths} months (${tenureLong(goal.years, goal.months)})`,
  },
  { label: "Goal date", value: on(goal.goalDate) },
  { label: "Your age then", value: ageAtLabel(ctx.plan, goal.totalMonths) },
  {
    label: "Asset segment",
    value: `${goal.seg} — ${goal.segTerm
      .toLowerCase()
      .replace(/\((\d+)-(\d+)\s*yr\)/, "($1–$2 years)")}`,
  },
  { label: "Return assumed", value: `${percent(goal.ret)} a year` },
  { label: "Amount needed on the goal date", value: inr(goal.futureCost) },
];

/* ───────────────────────── 04 · lumpsum deployment ──────────────────── */

export const deploymentLede = (
  ctx: ReportContext,
  goal: PlannerGoalResult
): string => {
  const bond = goal.bond!;
  const grade = bond.rating ? bond.rating.replace(/\s*by\s.*/i, "").trim() : "";
  const issuer = bondIssuerWord(bond.name);
  const described = grade ? `${grade}-rated ${issuer}` : issuer;
  return `Your ${inr(ctx.plan.summary.lumpsum)} buys ${pluralWords(bond.units, "unit", "units")} of ${article(described)} ${described} bond. You hold it for the principal, and its ${bond.freq.toLowerCase()} coupons are invested rather than left in your account.`;
};

export const bondHeader = (
  goal: PlannerGoalResult
): { title: string; meta: string } => {
  const bond = goal.bond!;
  return {
    title: bondName(bond.name),
    meta: [
      `ISIN ${bond.isin}`,
      bond.rating ? `Rated ${bond.rating}` : "No rating returned",
      bond.settlementDate ? `Settlement ${on(bond.settlementDate)}` : null,
    ]
      .filter(Boolean)
      .join("  ·  "),
  };
};

export const deploymentTiles = (goal: PlannerGoalResult): StatTile[] => {
  const bond = goal.bond!;
  const priced = typeof bond.cleanPrice === "number";
  const settled = typeof bond.settlementAmount === "number";
  return [
    { label: "Coupon rate", value: percent(bond.coupon, 2) },
    { label: "Frequency", value: bond.freq },
    {
      label: "Quoted yield",
      value: bond.ytm == null ? "—" : percent(bond.ytm, 2),
      caption: bond.pricedAtPar ? "valued at par" : undefined,
    },
    { label: "Credit rating", value: ratingLabel(bond.rating) ?? "Not returned" },
    {
      label: "Face value",
      value: inr(bond.totalFaceValue ?? bond.faceValue ?? bond.invested),
    },
    ...(priced ? [{ label: "Clean price", value: bond.cleanPrice.toFixed(2) }] : []),
    ...(settled
      ? [{ label: "Settlement amount", value: inr(bond.settlementAmount) }]
      : [{ label: "Amount invested", value: inr(bond.invested) }]),
    { label: "Left uninvested", value: inr(bond.idle) },
  ];
};

/** Null on the older response shape, which carried no settlement figures. */
export const settlementLedger = (goal: PlannerGoalResult): LedgerRow[] | null => {
  const bond = goal.bond!;
  if (
    typeof bond.settlementAmount !== "number" ||
    typeof bond.accruedInterest !== "number"
  ) {
    return null;
  }
  const priced = typeof bond.cleanPrice === "number";
  const accrual = accrualWindow(
    bond.cashflows.find((row) => row.type === "Interest")?.date,
    bond.freq,
    bond.settlementDate
  );
  return [
    {
      label: priced
        ? `${pluralWords(bond.units, "unit", "units")} at a clean price of ${bond.cleanPrice.toFixed(2)}`
        : `${pluralWords(bond.units, "unit", "units")} at the quoted price`,
      note: `face value ${inr(bond.totalFaceValue ?? bond.invested)}`,
      value: inr(bond.invested),
    },
    {
      label:
        bond.accruedInterest < 0
          ? "Less accrued interest kept by the seller"
          : "Plus accrued interest owed to the seller",
      note: accrual
        ? `${accrual.days} days since the ${on(isoDate(accrual.from))} coupon`
        : undefined,
      value: `${bond.accruedInterest < 0 ? "− " : ""}${inr(Math.abs(bond.accruedInterest))}`,
      negative: bond.accruedInterest < 0,
    },
    {
      label: `Settlement amount, ${on(bond.settlementDate)}`,
      value: inr(bond.settlementAmount),
    },
  ];
};

/* ────────────────────────── 05 · the coupon sweep ───────────────────── */

export const sweepParts = (ctx: ReportContext, goal: PlannerGoalResult) => {
  const bond = goal.bond!;
  const coupons = bond.cashflows.filter((row) => row.type === "Interest");
  const grossTotal = coupons.reduce((sum, row) => sum + row.amount, 0);
  const tdsPct =
    ctx.plan.assumptions?.tdsOnBondCouponPct ??
    (grossTotal > 0
      ? Math.round((1 - bond.totalInterestAfterTds / grossTotal) * 100)
      : 10);

  return {
    lede: `This bond pays ${bond.freq.toLowerCase() === "yearly" ? "once a year" : bond.freq.toLowerCase()} rather than monthly, so the sweep is ${pluralWords(coupons.length, "amount", "amounts")} rather than a monthly drip. Tax is withheld at source and the remainder is invested at the goal's ${percent(goal.ret)}.`,
    tiles: [
      {
        label:
          bond.freq === "Yearly" ? "Each year" : `Each ${bond.freq.toLowerCase()}`,
        value: inr(bond.avgCoupon),
        caption: `coupon, ${pluralWords(coupons.length, "payment", "payments")}`,
      },
      {
        label: `Less TDS ${Math.round(tdsPct)}%`,
        value: inr(bond.avgCouponAfterTds),
        caption: "net amount swept",
      },
      {
        label: `Invested at ${percent(goal.ret, 0)}`,
        value: `${coupons.length} ×`,
        caption: `${bond.freq.toLowerCase()} instalments`,
      },
      {
        label: "At goal",
        value: inr(bond.couponCorpusAtGoal),
        caption: "coupon corpus",
      },
    ] as StatTile[],
    totalLine: `Total interest over the life of the bond: ${inr(grossTotal)} gross · ${inr(bond.totalInterestAfterTds)} after TDS.`,
    ledger: [
      {
        label: "Coupon corpus at the goal date",
        value: inr(bond.couponCorpusAtGoal),
      },
      {
        label: bond.maturesBeforeGoal
          ? "Bond principal, parked to the goal date"
          : "Bond principal returned on redemption",
        value: inr(bond.principalAtGoal),
      },
      {
        label: `Uninvested cash grown at ${percent(goal.ret, 0)}`,
        value: inr(bond.idleAtGoal),
      },
      { label: "Total delivered by your lumpsum", value: inr(bond.bondRoute) },
    ] as LedgerRow[],
  };
};

/* ──────────────────────────── 06 · the gap ──────────────────────────── */

export const gapParts = (ctx: ReportContext, goalIndex: number) => {
  const goal = ctx.plan.goals[goalIndex];
  const stats = ctx.analytics.goals[goalIndex];
  const startIso = isoDate(addMonths(ctx.plan.generatedAt.slice(0, 10), 1));

  return {
    ledger:
      goal.coveredByLumpsum > 0
        ? ([
            {
              label: "Amount needed on the goal date",
              value: inr(goal.futureCost),
            },
            {
              label: "Less: delivered by your lumpsum",
              value: `− ${inr(goal.coveredByLumpsum)}`,
            },
            { label: "Gap to fund from monthly savings", value: inr(goal.gap) },
          ] as LedgerRow[])
        : null,
    tiles: [
      {
        label: `${goal.sipFreq} instalment`,
        value: inr(goal.sip),
        caption: "end of each period",
      },
      {
        label: "Instalments",
        value: String(stats.instalmentCount),
        caption: instalmentWindow(startIso, goal.goalDate),
      },
      {
        label: "Total you invest",
        value: inr(goal.sip * stats.instalmentCount),
        caption: `over ${goal.totalMonths} months`,
      },
      {
        label: "Growth earned",
        value: inr(Math.max(0, goal.gap - goal.sip * stats.instalmentCount)),
        caption: `at ${percent(goal.ret)} a year`,
      },
    ] as StatTile[],
  };
};

/* ───────────────────────── the fund basket table ────────────────────── */

export const fundsTable = (funds: PlannerFund[], frequency: string): TableSpec => {
  const anyScored = funds.some((fund) => fund.score != null);
  return {
    columns: [
      { header: "Fund", width: 40 },
      { header: "Category", width: 22 },
      ...(anyScored
        ? [{ header: "Score", width: 12, align: "right" as const }]
        : []),
      { header: "Weight", width: 11, align: "right" as const },
      { header: frequency, width: 15, align: "right" as const },
    ],
    rows: funds.map((fund) => {
      const cells: TableCellSpec[] = [
        cell(schemeName(fund.name), {
          note: `ISIN ${fund.isin}${fund.prime ? " · SMEST Prime" : ""}`,
          strong: true,
        }),
        cell(fund.family || "—", { muted: true }),
      ];
      if (anyScored) {
        cells.push(
          fund.score == null
            ? cell("not scored", { muted: true })
            : cell(fund.score.toFixed(2), { strong: true })
        );
      }
      cells.push(cell(`${Math.round(fund.weightPct)}%`));
      cells.push(cell(inr(fund.amount), { strong: true }));
      return cells;
    }),
  };
};

/* ───────────────────────── the loan cancel-out ──────────────────────── */

export const liabilityParts = (ctx: ReportContext, index: number) => {
  const liability = ctx.plan.liabilities[index];
  const liabilityFacts = ctx.facts.liabilities[index];
  const endIso = isoDate(
    addMonths(ctx.plan.generatedAt.slice(0, 10), liability.totalMonths)
  );
  const factorPct = (liabilityFacts.sip10 / liability.emi) * 100;
  const catchUp = liability.catchUp;
  const elapsedMonths = liability.elapsedMonths ?? 0;

  const sizingRows = [
    [
      cell(`The ${percent(factorPct, 0)}-of-EMI rule of thumb`, {
        note: `${percent(factorPct, 0)} × ${inr(liability.emi)}, invested in ${liability.seg.toLowerCase()} funds at ${percent(liability.ret)}`,
      }),
      cell(inr(liability.sip10)),
      cell(inr(liability.sip10FV)),
    ],
  ];
  if (catchUp) {
    sizingRows.push(
      [
        cell(`Catch-up for the ${elapsedMonths} months already passed`, {
          note: `rebuilds the ${inr(catchUp.elapsedFV)} the rule of thumb would have grown to since the loan began`,
        }),
        cell(inr(catchUp.topUpSip)),
        cell(inr(catchUp.elapsedFV)),
      ],
      [
        cell("Planned SIP", { strong: true }),
        cell(inr(catchUp.totalSip), { strong: true }),
        cell(inr(liability.sip10FV + catchUp.elapsedFV), { strong: true }),
      ]
    );
  } else {
    sizingRows.push([
      cell("Sized to cover the interest in full", { strong: true }),
      cell(inr(liability.sipNeeded), { strong: true }),
      cell(inr(liability.totalInterest), { strong: true }),
    ]);
  }

  return {
    lede: catchUp
      ? `Over the whole loan, including the ${elapsedMonths} months already run, it costs about ${inr(liability.totalInterest)} in interest — ${Math.round(liabilityFacts.interestSharePct)}% of the amount still outstanding. The plan does not try to reduce that. It sizes a separate monthly investment, with a catch-up for the time already passed, which by the time the loan is repaid should be worth roughly what the interest cost you.`
      : `Over ${liability.tenureLabel} this loan costs ${inr(liability.totalInterest)} in interest — ${Math.round(liabilityFacts.interestSharePct)}% of the amount borrowed. The plan does not try to reduce that. It sizes a separate monthly investment which, by the time the loan is repaid, should be worth roughly what the interest cost you.`,
    facts: [
      { label: "Outstanding", value: inr(liability.outstanding) },
      { label: "Interest rate", value: percent(liability.rate) },
      { label: "Monthly EMI", value: inr(liability.emi) },
      { label: "Remaining tenure", value: `${liability.totalMonths} months` },
      ...(catchUp
        ? [{ label: "Already run", value: `${elapsedMonths} months` }]
        : []),
      { label: "Loan ends", value: monthYear(endIso) },
      ...(catchUp
        ? [
            {
              label: "Interest still to pay",
              value: inr(liability.remainingInterest ?? 0),
            },
            {
              label: "Interest already paid (est.)",
              value: inr(liability.elapsedInterest ?? 0),
            },
          ]
        : []),
      {
        label: catchUp ? "Total interest, whole loan" : "Total interest",
        value: inr(liability.totalInterest),
      },
    ] as FactRow[],
    sizing: {
      caption: "Sizing the offset",
      columns: [
        { header: "Approach", width: 58 },
        { header: "Monthly", width: 21, align: "right" as const },
        { header: `Worth in ${endIso.slice(0, 4)}`, width: 21, align: "right" as const },
      ],
      rows: sizingRows,
      footnote: catchUp
        ? liability.cancels
          ? `The planned SIP covers the ${inr(liability.totalInterest)} of interest over the whole loan.`
          : `The planned SIP falls ${inr(liability.totalInterest - (liability.sip10FV + catchUp.elapsedFV))} short of the ${inr(liability.totalInterest)} of interest over the whole loan.`
        : liability.cancels
          ? undefined
          : `The rule of thumb falls ${inr(liability.totalInterest - liability.sip10FV)} short, so the plan uses the larger figure — an extra ${inr(liability.sipNeeded - liability.sip10)} a month.`,
    } as TableSpec,
  };
};

/* ────────────────────────── 11 · retirement income ──────────────────── */

export const retirementParts = (ctx: ReportContext) => {
  const swp = ctx.plan.swp!;
  const adequacy = ctx.adequacy;
  const period = swp.freq.toLowerCase();

  return {
    lede: `From ${monthYear(swp.startDate)} you plan to draw ${inr(swp.withdrawal)} a ${period.replace("ly", "")} from the corpus this plan builds.${swp.pension > 0 ? ` A pension of ${inr(swp.pension)} covers part of it.` : " There is no pension and no separate lumpsum, so the whole withdrawal falls on that corpus."}`,
    facts: [
      { label: "Drawdown starts", value: on(swp.startDate) },
      ...(adequacy
        ? [{ label: "Your age then", value: `${Math.floor(adequacy.startAge)} years` }]
        : []),
      { label: "Starting corpus", value: inr(swp.startCorpus) },
      { label: "Pension", value: inr(swp.pension) },
      { label: `${swp.freq} withdrawal`, value: inr(swp.net) },
      ...(adequacy
        ? [
            {
              label: "As a share of the corpus",
              value: `${percent(adequacy.withdrawalRatePct)} a year`,
            },
          ]
        : []),
      {
        label: "Return / inflation",
        value: `${percent(swp.ret, 0)} / ${percent(swp.infl, 0)}`,
      },
      ...(adequacy
        ? [
            {
              label: "Real return after inflation",
              value: percent(adequacy.realReturnPct, 2),
            },
          ]
        : []),
    ] as FactRow[],
  };
};

/* ───────────────────────── 12 · what it would take ──────────────────── */

/**
 * The corpus comparison.
 *
 * Every required bar carries `covered` — the part of it the plan already
 * builds — so the shortfall can be shown as the remainder of the same bar
 * rather than as a separate one. Four bars of different lengths only tell a
 * reader that the last three are longer; a bar split at the point the money
 * runs out tells them how much is missing, which is the finding.
 *
 * `multiple` is the same fact as a number: "this needs 3.7× what you have".
 */
export const adequacyBars = (ctx: ReportContext) => {
  const swp = ctx.plan.swp!;
  const adequacy = ctx.adequacy!;
  const built = adequacy.built;
  return {
    caption: `Corpus needed to sustain ${inr(swp.net)} a ${swp.freq.toLowerCase().replace("ly", "")}`,
    built,
    rows: [
      {
        label: "This plan builds",
        value: inr(built),
        amount: built,
        covered: built,
        shortfall: 0,
        multiple: null as number | null,
        reference: true,
      },
      ...adequacy.rows.map((row) => ({
        label: `To last ${row.years} years`,
        value: inr(row.corpusNeeded),
        amount: row.corpusNeeded,
        covered: Math.min(built, row.corpusNeeded),
        /* The figure the section is named after, and the one a bar cannot
           show: not what is needed, but what is missing. */
        shortfall: Math.max(0, row.corpusNeeded - built),
        multiple: built > 0 ? row.corpusNeeded / built : null,
        reference: false,
      })),
    ],
  };
};

export const adequacyTable = (ctx: ReportContext): TableSpec => {
  const swp = ctx.plan.swp!;
  const adequacy = ctx.adequacy!;
  return {
    caption: "Or, keeping the corpus as it is, what it can safely pay",
    columns: [
      { header: "If the corpus must last", width: 46 },
      { header: "Sustainable monthly draw", width: 30, align: "right" },
      { header: "Share of planned", width: 24, align: "right" },
    ],
    rows: [
      ...adequacy.rows.map((row) => [
        cell(`${row.years} years, to age ${row.toAge}`),
        cell(inr(row.draw), { strong: true }),
        cell(`${Math.round(row.drawSharePct)}%`),
      ]),
      [
        cell(
          `${tenureLong(
            Math.floor(adequacy.lastsYears),
            Math.round((adequacy.lastsYears % 1) * 12)
          )}, to age ${adequacy.exhaustedAge}`,
          { strong: true }
        ),
        cell(inr(adequacy.planned), { strong: true }),
        cell("100%", { strong: true, alarm: true }),
      ],
    ],
  };
};

/* ───────────────────────── 13 · the monthly picture ─────────────────── */

export const monthlyTable = (ctx: ReportContext): TableSpec => {
  const { plan, facts, analytics } = ctx;
  return {
    columns: [
      { header: "Commitment", width: 40 },
      { header: "Runs until", width: 38 },
      { header: "Monthly", width: 22, align: "right" },
    ],
    rows: [
      ...plan.goals.map((goal, index) => [
        cell(`${goal.name} SIP`, { strong: true }),
        cell(
          `${monthYear(goal.goalDate)} · ${analytics.goals[index].instalmentCount} instalments`,
          { muted: true }
        ),
        cell(inr(analytics.goals[index].monthlySip), { strong: true }),
      ]),
      ...plan.liabilities.map((liability) => {
        const endIso = isoDate(
          addMonths(plan.generatedAt.slice(0, 10), liability.totalMonths)
        );
        return [
          cell(`${liability.name} offset SIP`, { strong: true }),
          cell(`${monthYear(endIso)} · ${liability.totalMonths} instalments`, {
            muted: true,
          }),
          cell(inr(liabilityMonthlySip(liability)), { strong: true }),
        ];
      }),
      [
        cell("Total the plan asks for", { strong: true }),
        cell(""),
        cell(inr(facts.committed), { strong: true }),
      ],
      [
        cell("Your recorded monthly surplus"),
        cell(""),
        cell(inr(facts.surplus), { strong: true }),
      ],
      [
        cell(facts.shortfall > 0 ? "Shortfall" : "Left over each month", {
          strong: true,
        }),
        cell(""),
        cell(inr(facts.shortfall > 0 ? facts.shortfall : facts.spare), {
          strong: true,
          alarm: facts.shortfall > 0,
        }),
      ],
    ],
    footnote: plan.liabilities.length
      ? `The ${inr(plan.liabilities[0].emi)} ${plan.liabilities[0].name} EMI is not counted here — it is assumed paid before the surplus is measured. Counting it, your total monthly commitment to this plan and that loan together would be ${inr(facts.committed + plan.liabilities.reduce((sum, l) => sum + l.emi, 0))}.`
      : undefined,
  };
};

/* ────────────────────────────── 14 · orders ─────────────────────────── */

export const orderTables = (ctx: ReportContext) => {
  const orders = ctx.plan.orders ?? [];
  const oneOff = orders.filter((order) => order.kind !== "mutualFundSip");
  const sips = orders.filter((order) => order.kind === "mutualFundSip");
  const sipTotal = sips.reduce((sum, order) => sum + order.amount, 0);

  return {
    lede: `If this plan were executed as it stands, these are the ${words(orders.length)} instructions it produces. They are shown for completeness — please read the sections above before placing any of them.`,
    oneOff: oneOff.length
      ? ({
          caption: "One-time",
          columns: [
            { header: "Instrument", width: 44 },
            { header: "For", width: 20 },
            { header: "Settles", width: 16 },
            { header: "Amount", width: 20, align: "right" as const },
          ],
          rows: oneOff.map((order) => [
            cell(order.bond ? bondName(order.name) : schemeName(order.name), {
              note: [
                `ISIN ${order.isin}`,
                order.bond ? pluralWords(order.bond.units, "unit", "units") : null,
              ]
                .filter(Boolean)
                .join(" · "),
              strong: true,
            }),
            cell(order.source.name, { muted: true }),
            cell(
              order.bond?.settlementDate ? on(order.bond.settlementDate) : "On payment",
              { muted: true }
            ),
            cell(inr(order.amount), { strong: true }),
          ]),
        } as TableSpec)
      : null,
    sips: sips.length
      ? ({
          caption: `${sips[0].sip?.frequency ?? "Monthly"} SIPs`,
          columns: [
            { header: "Fund", width: 44 },
            { header: "For", width: 20 },
            { header: "Instalments", width: 16, align: "right" as const },
            { header: "Each", width: 20, align: "right" as const },
          ],
          rows: [
            ...sips.map((order) => [
              cell(schemeName(order.name), {
                note: `ISIN ${order.isin}`,
                strong: true,
              }),
              cell(order.source.name, { muted: true }),
              cell(String(order.sip?.installments ?? "—")),
              cell(inr(order.amount), { strong: true }),
            ]),
            [
              cell("Total per instalment", { strong: true }),
              cell(""),
              cell(""),
              cell(inr(sipTotal), { strong: true }),
            ],
          ],
          footnote:
            Math.round(sipTotal) === Math.round(ctx.facts.committed)
              ? undefined
              : `The order total differs from the plan total of ${inr(ctx.facts.committed)} by ${inr(Math.abs(sipTotal - ctx.facts.committed))}, through rounding of individual instalments.`,
        } as TableSpec)
      : null,
  };
};

/* ─────────────────────────────── 15 · tax ───────────────────────────── */

export const taxTable = (ctx: ReportContext): TableSpec => {
  const { plan } = ctx;
  const slab = plan.client.taxBracket;
  const tdsPct = plan.assumptions?.tdsOnBondCouponPct ?? 10;
  const rows: TableCellSpec[][] = [];

  const bondGoal = plan.goals.find((goal) => goal.bond);
  if (bondGoal?.bond) {
    rows.push([
      cell("Bond coupons", { strong: true }),
      cell(
        `Interest income, taxed at your ${slab}% slab. TDS of ${Math.round(tdsPct)}% is withheld by the issuer at each payment — that is not the final liability.`
      ),
    ]);
    const premium = bondGoal.bond.invested - bondGoal.bond.totalFaceValue;
    rows.push([
      cell("Bond principal", { strong: true }),
      cell(
        `Return of capital on redemption — not taxable.${
          Math.abs(premium) > 1
            ? ` The bond was bought ${premium > 0 ? "above" : "below"} face value, so there is a capital ${premium > 0 ? "loss" : "gain"} of ${inr(Math.abs(premium))} on redemption.`
            : ""
        }`
      ),
    ]);
  }

  /* One row per distinct scheme, not per holding — the same fund bought for two
     objectives is one tax position, and listing it twice reads as two. */
  const seen = new Map<string, { fund: PlannerFund; where: string[] }>();
  plan.goals.forEach((goal) =>
    goal.funds.forEach((fund) => {
      const entry = seen.get(fund.isin) ?? { fund, where: [] };
      entry.where.push(goal.name);
      seen.set(fund.isin, entry);
    })
  );
  plan.liabilities.forEach((liability) =>
    liability.funds.forEach((fund) => {
      const entry = seen.get(fund.isin) ?? { fund, where: [] };
      entry.where.push(`${liability.name} offset`);
      seen.set(fund.isin, entry);
    })
  );

  seen.forEach(({ fund, where }) => {
    rows.push([
      cell(schemeName(fund.name).replace(/^[A-Z]+ /, ""), {
        note: where.join(" + "),
        strong: true,
      }),
      cell(
        fund.tax === "equity"
          ? "Equity taxation: 12.5% LTCG on gains above ₹1.25 lakh a year when held over a year; 20% STCG below that."
          : `Debt taxation: gains are added to income and taxed at your ${slab}% slab, whatever the holding period.`
      ),
    ]);
  });

  if (plan.swp) {
    rows.push([
      cell("SWP withdrawals", {
        note: monthYear(plan.swp.startDate),
        strong: true,
      }),
      cell(
        "Each withdrawal is part capital and part gain; only the gain is taxed, at the rate applying to that fund."
      ),
    ]);
  }

  return {
    columns: [
      { header: "Source", width: 26 },
      { header: "Treatment", width: 74 },
    ],
    rows,
    footnote:
      "Tax rules change and depend on facts specific to the assessee. Your tax adviser should confirm every position on this page before you act on it.",
  };
};

/* ──────────────────── appendix B · cash flows, assumptions ──────────── */

export const cashflowTable = (ctx: ReportContext): TableSpec | null => {
  const goal = ctx.plan.goals.find((candidate) => candidate.bond?.cashflows.length);
  const bond = goal?.bond;
  if (!bond) return null;
  const coupons = bond.cashflows.filter((row) => row.type === "Interest");
  const tds = Math.round(ctx.plan.assumptions?.tdsOnBondCouponPct ?? 10);

  return {
    caption: `${bondIssuer(bond.name)} — scheduled cash flows`,
    columns: [
      { header: "Date", width: 30 },
      { header: "Type", width: 22 },
      { header: "Gross", width: 24, align: "right" },
      { header: `After ${tds}% TDS`, width: 24, align: "right" },
    ],
    rows: [
      ...bond.cashflows.map((row) => [
        cell(on(row.date)),
        cell(row.type, { muted: true }),
        cell(inr(row.amount)),
        cell(inr(row.afterTds), { strong: true }),
      ]),
      [
        cell("Interest total", { strong: true }),
        cell(""),
        cell(inr(coupons.reduce((sum, row) => sum + row.amount, 0)), {
          strong: true,
        }),
        cell(inr(bond.totalInterestAfterTds), { strong: true }),
      ],
    ],
    footnote:
      "Redemption is a return of capital, so no TDS applies. Coupon amounts vary slightly with the day count in each period.",
  };
};

export const assumptionsTable = (
  ctx: ReportContext,
  config?: { segBoundaries?: { shortMaxYears: number; midMaxYears: number }; defaultReturn?: Record<string, number>; inflationDefault?: number }
): TableSpec | null => {
  const a = ctx.plan.assumptions;
  const rows: TableCellSpec[][] = [];
  const add = (label: string, value: string | null | undefined): void => {
    if (value) rows.push([cell(label, { strong: true }), cell(value)]);
  };

  const boundaries = a?.segmentBoundaries ?? config?.segBoundaries;
  if (boundaries) {
    add("Short-term horizon", `0–${boundaries.shortMaxYears} years`);
    add(
      "Medium-term horizon",
      `${boundaries.shortMaxYears}–${boundaries.midMaxYears} years`
    );
    add("Long-term horizon", `${boundaries.midMaxYears} years and beyond`);
  }
  const returns = a?.defaultReturnBySegment ?? config?.defaultReturn;
  if (returns) {
    add("Return — debt", percent(returns.Debt));
    add("Return — hybrid", percent(returns.Hybrid));
    add("Return — equity", percent(returns.Equity));
  }
  add(
    "Goal inflation",
    percent(a?.inflationDefault ?? config?.inflationDefault ?? null)
  );
  add(
    "TDS on bond coupons",
    a?.tdsOnBondCouponPct == null ? null : `${a.tdsOnBondCouponPct}%`
  );
  add(
    "Idle cash parked at",
    a?.debtParkingRatePct == null ? null : percent(a.debtParkingRatePct)
  );
  add(
    "Maximum exposure to one fund house",
    a?.maxAmcExposurePct == null ? null : `${a.maxAmcExposurePct}%`
  );
  add(
    "Loan offset SIP",
    a?.liabilitySipFactorPct == null
      ? null
      : `${a.liabilitySipFactorPct}% of the EMI`
  );
  add("Bond pricing", a?.bondPricing);
  add("Bond quantity", a?.bondQuantity);
  add("Bond yield", a?.bondYield);
  if (a?.bondEligibilityWindow) {
    add(
      "Bond eligibility window",
      `maturing up to ${a.bondEligibilityWindow.beforeMonths} months before the goal, or ${a.bondEligibilityWindow.afterDays} days after`
    );
  }
  if (a?.smestScoreWeights) {
    add("SMEST score", smestWeightsText(a.smestScoreWeights, a.smestNeutralFill));
  }

  if (!rows.length) return null;
  return {
    caption: "Assumptions this plan rests on",
    columns: [
      { header: "Assumption", width: 38 },
      { header: "Value", width: 62 },
    ],
    rows,
    footnote:
      "Every return shown in this document is an assumption, not a guarantee. Mutual funds carry market risk; bond prices are indicative at par where no live price is available.",
  };
};

/**
 * The engine writes its workings in ASCII — "Rs 50,00,000 x (1 + 8.0%)^4y 5m".
 * The arithmetic is reproduced without alteration; only the glyphs are set
 * properly, so the appendix reads like the rest of the document.
 */
export const setWorking = (formula: string): string =>
  formula
    .replace(/\bRs\s?/g, "₹")
    .replace(/\s->\s/g, " → ")
    .replace(/(\d)\s*x\s*(\d|\()/g, "$1 × $2")
    .replace(/\bx\s(?=[A-Z₹])/g, "× ")
    .replace(/\s{2,}/g, " ")
    .trim();

/** The parts Appendix A walks, in the order the document numbers them. */
export const tracesByPart = (
  ctx: ReportContext
): { title: string; trace: PlanResult["goals"][number]["trace"] }[] => {
  const out: { title: string; trace: PlanResult["goals"][number]["trace"] }[] = [];
  let part = 0;
  ctx.plan.goals.forEach((goal) => {
    out.push({ title: `Part ${partWord(part)} — ${goal.name}`, trace: goal.trace });
    part += 1;
  });
  ctx.plan.liabilities.forEach((liability) => {
    out.push({
      title: `Part ${partWord(part)} — ${liability.name}`,
      trace: liability.trace,
    });
    part += 1;
  });
  if (ctx.plan.swp?.trace.length) {
    out.push({
      title: `Part ${partWord(part)} — Retirement Income`,
      trace: ctx.plan.swp.trace,
    });
  }
  return out;
};
